import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BookmarkSimple, Trash } from "@phosphor-icons/react";

/** Signed-in reading list of bookmarked stories. Used in Dashboard. */
export default function ReadingList() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    http.get("/bookmarks")
      .then(r => setItems(r.data.bookmarks || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (story_id) => {
    try {
      await http.delete(`/bookmarks/${story_id}`);
      setItems(l => l.filter(b => b.story_id !== story_id));
      toast.success("Removed.");
    } catch { toast.error("Could not remove."); }
  };

  return (
    <section data-testid="reading-list-section" className="py-16 md:py-24 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-10">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/ My reading list</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[0.95] tracking-tighter flex items-center gap-3">
            <BookmarkSimple weight="fill" className="text-[color:var(--np-brand)]" size={40} />
            Saved for later.
          </h2>
        </div>
        <p className="md:col-span-6 md:col-start-7 self-end text-[color:var(--np-text-2)] text-lg">
          Every story you bookmark lives here — even the ones the AI wrote live. Pick up where you left off.
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="bg-black/5 h-40 animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-black/20 py-20 text-center font-mono-tech text-sm tracking-widest uppercase text-[color:var(--np-text-2)]">
          No bookmarks yet. Open a story and tap Save.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
          {items.map((b, i) => (
            <motion.div
              key={b.bookmark_id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.03 }}
              className="bg-[color:var(--np-bg)] p-6 flex flex-col min-h-[220px]"
              data-testid={`bookmark-card-${i}`}
            >
              <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-3">{b.region}</div>
              <h3 className="font-serif-display text-2xl leading-tight tracking-tight mb-4">{b.topic}</h3>
              <div className="mt-auto flex items-center justify-between font-mono-tech text-[10px] tracking-[0.25em] uppercase">
                <button onClick={() => setOpen(b.story)} className="text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]" data-testid={`bookmark-read-${i}`}>Read →</button>
                <button onClick={() => remove(b.story_id)} className="text-[color:var(--np-alert)] hover:text-[color:var(--np-brand)] inline-flex items-center gap-1" data-testid={`bookmark-remove-${i}`}>
                  <Trash size={12} /> Remove
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(null)}>
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl w-full bg-[color:var(--np-bg)] border border-black/15 p-8 md:p-12 my-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setOpen(null)} className="float-right font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]">Close ✕</button>
            <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-4">{open.region}</div>
            <h3 className="font-serif-display text-4xl md:text-5xl leading-[1] tracking-tight mb-8">{open.topic}</h3>
            <div className="text-[color:var(--np-text)] whitespace-pre-wrap font-body text-base leading-relaxed">{open.content}</div>
          </motion.article>
        </div>
      )}
    </section>
  );
}
